import { list } from '../../../../../../common/classes/request-builder';
import { StorageManager } from 'src/app/common/storage-manager.class';
import { checkIfRolAdmin } from 'src/app/common/check-role';

import { SectorModel } from 'src/app/models/form-common/sector.model';
import { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import { FincasModel } from 'src/app/models/form-common/fincas.module';
import { MantenimientoModel } from 'src/app/models/trabajadores/mantenimiento.model';
import { FertilizantesEnvironmentDefinition } from '../../environment-variables';
import { FertilizantesListsDefinition } from './list-variables';
import {
    Form,
    FormField,
    FormSection,
    FormTable,
    FormType,
    InputType,
    RepeatableComputeContext,
    ReactiveFormChangedField,
    ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';
import { TareaFertiModel } from 'src/app/models/tareas/tarea-ferti.model';
import moment from 'moment';
import { BuildNPKTable } from './buil-NPK-table';
import { Utils } from 'src/app/common/utils';
import { MaquinariaModel } from 'src/app/models/trabajadores/maquinaria.model';
import type { FormGroup } from '@angular/forms';
import {
  createActiveCatalogueLegacyFilter,
  createFieldEqualsLegacyFilter,
  createClientScopedLegacyFilter,
  createClientScopedOptionFilter,
  createClientScopedParams,
  createFincaScopedLegacyFilter,
  createFincaScopedOptionFilter,
  createFincaScopedParams,
  createParcelasScopedLegacyFilter,
  createParcelasScopedOptionFilter,
  createTareasLocationDropdownDataSource,
  tareasLocationDependencyBehaviour,
  tareasLocationFincaDependencyBehaviour,
} from '../../../shared/tareas-location-dropdown.datasource';


export class FertilizantesFormDefinition {

  public disabledDates: Date[];

  public formType!: FormType;

  public env: FertilizantesEnvironmentDefinition = new FertilizantesEnvironmentDefinition();

  public lists: FertilizantesListsDefinition = new FertilizantesListsDefinition();

  public npkBuilder: BuildNPKTable = new BuildNPKTable();

  public formFields!: Form<TareaFertiModel>;

  public fieldNames: FormField<any>[] = [];

  public fieldLabels: string[] = [];

  public lastFieldEdited = '';

  public tag: ReactiveFormChangedField<TareaFertiModel> = '';

  public kgPorSectorTabIndex = 0;

  public requestType: FormType = FormType.CREATION;

  public cliente = StorageManager.getClient();

  public usuario = StorageManager.getUser();

  public sessionData = StorageManager.getSessionDataDonJardin()[0];

  public model: ReactiveFormValue<TareaFertiModel> = {} as ReactiveFormValue<TareaFertiModel> ;

  public calculoSectores: Record<string, number> = {};

  public horaInicio = '';

  public horaFin = '';

  public now = new Date();

  public day = this.now.getDate().toString().padStart(2, '0');

  public month = (this.now.getMonth() + 1).toString().padStart(2, '0');

  public year = this.now.getFullYear().toString();

  public hours = this.now.getHours().toString().padStart(2, '0');

  public minutes = this.now.getMinutes().toString().padStart(2, '0');

  private readonly zeroLabel = '0,00';

  public getFormFieldsSectorLeft(): FormField<any>[] {
    const formFieldsSectorLeft: FormField<any>[] = [
      {
        name: this.formType < 3 ? 'id_finca' : 'finca',
        vinculatedFields: undefined,
        label: this.env.fincaName,
        input: {
          type: InputType.DROPDOWN_SEARCH,
          isEditable: true,
          defaultValue: 
            this.env.showTareasMto && this.formType === FormType.CREATION && 
            this.sessionData !== undefined ? this.sessionData : undefined,
          data: this.lists.fincas,
          dropdownConfiguration: {
            valuePrimaryKey: 'id',
            filter: createClientScopedLegacyFilter<TareaFertiModel>({
              isEnabled: () => this.env.hasClients && this.formType !== FormType.EDITION && this.env.userCanSeeClientes,
            }),
          }
        },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: true,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.hasFincas,
        },
        order: 1
      },
      {
        name: this.formType < 3 ? 'ids_sectores' : 'sectores_nombres',
        dependencies: [
            {
                source: 'id_finca',
                target: this.formType < 3 ? 'ids_sectores' : 'sectores_nombres',
                kind: 'filter'
            }
        ],
        vinculatedFields: undefined,
        label: this.env.sectoresName,
        input: {
          type: this.env.showTareasFertiSectorMultiple
            ? InputType.MULTISELECT_SEARCH
            : InputType.DROPDOWN_SEARCH,
          isEditable: true,
          data: this.lists.sectores,
          dropdownConfiguration: {
            valuePrimaryKey: 'id',
            filter: createFincaScopedLegacyFilter<TareaFertiModel>(),
          }
        },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showSectores && this.env.showFertilizantesSectores,
        },
        order: 2
      },
      {
        name: this.formType < 3 ? 'id_parcelas' : 'parcelas_nombres',
        dependencies: [
            {
                source: 'id_finca',
                target: this.formType < 3 ? 'id_parcelas' : 'parcelas_nombres',
                kind: 'filter'
            },
            {
                source: 'ids_sectores',
                target: this.formType < 3 ? 'id_parcelas' : 'parcelas_nombres',
                kind: 'filter'
            }
        ],
        vinculatedFields: undefined,
        label: this.env.parcelasName,
        input: {
          type: InputType.MULTISELECT_SEARCH,
          isEditable: true,
          data: this.lists.parcelas,
          dropdownConfiguration: {
            summation: {
              summand: 'superficie_cultivada',
              name: this.env.tipoSuperficieParcelas,
              uds: this.env.textoSuperficie,
              format: (total: number) =>
                total.toLocaleString('es-ES', {
                  useGrouping: true,
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })
            },

            valuePrimaryKey: 'id',
            filter: createParcelasScopedLegacyFilter<TareaFertiModel>({
              sectorFieldName: 'ids_sectores',
              isSectorFilterEnabled: () => this.env.showFertilizantesSectores,
            }),
          }
        },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: this.env.appName !== 'pyf' && this.env.appName !== 'donjardin',
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showFertilizantesParcelas
        },
        order: 3
      },
      {
        name: 'facturado',
        vinculatedFields: undefined,
        label: 'Facturado',
        input: { type: InputType.SWITCH, isEditable: true },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.appName === 'agrosalvi'
        },
        order: 4
      },
      {
        name: 'fecha_inicio',
        vinculatedFields: undefined,
        label: 'Fecha Inicio',
        input: { type: InputType.CALENDAR, isEditable: true },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: this.env.appName === 'agrotata',
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: !this.env.showTareasMto
        },
        order: 5
      },
      {
        name: 'fecha_fin',
        vinculatedFields: undefined,
        label: this.env.showTareasMto ? 'Fecha' : 'Fecha Fin',
        input: {
          type: InputType.CALENDAR,
          isEditable: true,
          defaultValue: ((this.env.horaFechaFinActualPorDefecto &&
            this.formType !== FormType.EDITION &&
            this.env.appName !== 'sirga' &&
            this.env.appName !== 'yagoaznar') || this.env.appName === 'tomega' &&
            this.formType === FormType.EDITION) ? `${this.day}/${this.month}/${this.year}` : undefined,
          calendarConfiguration: {
            calendarView: 'date',
            calendarDateFormat: 'dd/mm/yy',
            disabledDates: [],
            onSelect: (date: Date): undefined => {
              if (this.env.appName !== 'detorres') {
                return;
              }

              const matches = this.eventos.filter((e) => {
                const parts = e.start.split('-');

                const requiredLength = 3;

                if (parts.length !== requiredLength) {
                  return false;
                }

                const d = Number(parts[0]);
                const m = Number(parts[1]);
                const y = Number(parts[2]);

                return (
                  date.getFullYear() === y &&
                  date.getMonth() === m - 1 &&
                  date.getDate() === d
                );
              });

              if (matches.length === 0) {
                return;
              }

              const mensaje = matches.map(
                (ev, i) => `Evento ${i + 1}:\n` +
                  `• Título: ${ev.title}\n` +
                  `• Hora inicio: ${ev.hora_inicio}\n` +
                  `• Hora fin: ${ev.hora_fin}`
              ).join('\n\n');

              alert(mensaje);
            },
          },
        },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: this.env.appName === 'agrotata',
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showTareasMto || this.env.activateREA
        },
        order: 6
      },
      {
        name: 'hora_inicio',
        vinculatedFields: undefined,
        label: 'Hora Inicio',
        input: { type: InputType.TIME, isEditable: true },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: this.env.activateRequireHourForRolUser || this.env.appName === 'agrotata',
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.timeStartStopTareas || this.env.showTareasFertiHoras,
        },
        order: 7
      },
      {
        name: 'hora_fin',
        vinculatedFields: undefined,
        label: 'Hora Fin',
        input: {
          type: InputType.TIME,
          isEditable: true,
          defaultValue: (
            this.env.horaFechaFinActualPorDefecto ||
            this.env.showTareasMto && this.usuario.rol === 'user'
          ) ?
            this.formType !== FormType.EDITION ? `${this.hours}:${this.minutes}` : undefined : undefined,
        },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: !(this.env.showTareasMto &&
            this.formType === FormType.DUPLICATION),
          isRequired: this.env.activateRequireHourForRolUser || this.env.appName === 'agrotata',
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.timeStartStopTareas || this.env.showTareasFertiHoras,
        },
        order: 8
      },
      {
        name: 'horas_totales',
        vinculatedFields: ['hora_inicio', 'hora_fin'],
        label: 'Horas',
        input: { type: InputType.ONLY_TEXT, isEditable: true },
        computed: {
          dependsOn: ['hora_inicio', 'hora_fin'],
          resolve: (context) => this.formatoHorasMinutos(
            String(context.getValue('hora_inicio') ?? this.horaInicio ?? '00:00'),
            String(context.getValue('hora_fin') ?? this.horaFin ?? '00:00'),
          ),
        },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showTareasMto || this.env.tareasHorasTotales || this.env.showTareasFertiHoras,
        },
        order: 9
      },
      {
        name: 'horas_totales',
        vinculatedFields: undefined,
        label: 'Horas',
        input: { type: InputType.NUMERIC, isEditable: true },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.appName === 'fotoagricultura',
        },
        order: 10
      },
      {
        name: 'tipo_fertilizacion',
        vinculatedFields: undefined,
        label: 'Tipo Fertilización',
        input: {
          type: InputType.DROPDOWN_SEARCH,
          isEditable: true,
          data: this.env.appName === 'covidai'
            ? this.lists.tipo_fertilizacion_covidai
            : (this.env.applicationType === 'cropgest' || this.env.activateREA)
              ? this.lists.tiposFertilizacionSIEX
              : this.lists.tipo_fertilizacion,
          dropdownConfiguration: {
            valuePrimaryKey: 'codigo_SIEX',
          }
        },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 11
      },
      {
        name: 'albaran_factura',
        vinculatedFields: undefined,
        label: this.env.appName === 'agrosalvi' ? 'Factura/Albarán' : 'Albarán/factura',
        input: { type: InputType.TEXT, isEditable: true },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 12
      },
      {
        name: 'prioridad',
        vinculatedFields: undefined,
        label: 'Prioridad/Realizada',
        input: {
          type: InputType.DROPDOWN_SEARCH,
          isEditable: true,
          data: this.lists.prioridades
        },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showTareasMto,
        },
        order: 12
      },
      {
        name: 'procura',
        vinculatedFields: undefined,
        label: 'Procura',
        input: {
          type: InputType.DROPDOWN_SEARCH,
          isEditable: true,
          data: list(['...', null], 'Procura', 'Media procura', 'Libre')
        },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showTareasProcura,
        },
        order: 13
      },
      {
        name: 'horas_dia',
        vinculatedFields: undefined,
        label: 'Horas/día',
        input: { type: InputType.NUMERIC, isEditable: true },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: !this.env.showFertilizantesFormShort,
        },
        order: 14
      },
      {
        name: 'numero_dias',
        vinculatedFields: undefined,
        label: 'Días/semana',
        input: { type: InputType.NUMERIC, isEditable: true },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: !this.env.showFertilizantesFormShort,
        },
        order: 15
      },
      {
        name: 'dias_semana',
        vinculatedFields: undefined,
        label: 'Días',
        input: {
          type: InputType.SELECT_BUTTON,
          isEditable: true,
          data: this.lists.weekDays
        },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showTareasFertiDias && !this.env.showFertilizantesFormShort,
        },
        order: 16
      },
      {
        name: 'nombre_cuba',
        vinculatedFields: undefined,
        label: 'Nombre/Cuba',
        input: { type: InputType.TEXT, isEditable: true },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showFertiNombreCuba,
        },
        order: 17
      },
      {
        name: 'consumo_agua',
        vinculatedFields: undefined,
        label: 'Consumo Agua m³',
        input: { type: InputType.TEXT, isEditable: true },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showTareasFertiConsumoAgua,
        },
        order: 18
      },
      {
        name: 'notas_cliente',
        vinculatedFields: undefined,
        label: 'Notas cliente',
        input: {
          type: InputType.TEXTAREA,
          defaultValue: this.formType === FormType.CREATION ? StorageManager.getClient()?.notas : null,
          isEditable: true
        },
        width: 6,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: true,
          hasUndoEnabled: false,
          isVisible: this.env.showClientesNotas,
        },
        order: 19
      },
      {
        name: this.formType < 3 ? 'ids_trabajadores' : 'trabajadores_nombres',
        vinculatedFields: undefined,
        label: 'Profesionales',
        input: {
          type: InputType.MULTISELECT,
          isEditable: true,
          data: this.lists.trabajadores,
          defaultValue:
            this.env.rol === 'user' &&
              this.formType === FormType.CREATION ?
              this.model['ids_trabajadores'] = this.usuario.id.toString() : undefined,
          dropdownConfiguration: {
            valuePrimaryKey: 'id',
            filter: createActiveCatalogueLegacyFilter<unknown, MantenimientoModel>({
              activeFieldName: 'activo',
              activeValue: 1,
            }),
          }
        },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showTareasMto || this.env.appName === 'fotoagricultura' || this.env.showTrabajadoresFertilizantes,
        },
        order: 20
      },
      {
        name: this.formType < 3 ? 'id_maquinaria' : 'maquinaria',
        vinculatedFields: undefined,
        label: 'Maquinaria',
        input: {
          type: InputType.DROPDOWN_SEARCH,
          isEditable: true,
          data: this.lists.maquinarias,
          dropdownConfiguration: {
            valuePrimaryKey: 'id',
            filter: createActiveCatalogueLegacyFilter<unknown, MaquinariaModel>({
              activeFieldName: 'activo',
              activeValue: '1',
            }),
          }
        },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showTareasFitoMaquinarias,
        },
        order: 21
      },
      {
        name: 'aplicado',
        vinculatedFields: undefined,
        label: 'Aplicado',
        input: {
          type: InputType.SWITCH,
          isEditable: true,
          defaultValue: this.env.showTareasMto && this.env.rol === 'user'
        },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showTareasMto && this.env.showTareasAplicados,
        },
        order: 22
      },
      {
        name: 'ids_asignados',
        vinculatedFields: undefined,
        label: 'Asignador',
        input: {
          type: InputType.MULTISELECT,
          isEditable: true,
          data: this.lists.users,
          dropdownConfiguration: {
            valuePrimaryKey: 'id'
          }
        },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: true,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: (this.env.showAssignUserTasksFertilizante ||
            this.env.showAssignUserInAllTasks) && this.env.rol === 'admin',
        },
        order: 23
      },
      {
        name: 'codigo_metodo_aplicacion',
        vinculatedFields: undefined,
        label: 'Métodos de Aplicación Fertilizantes',
        input: {
          type: InputType.DROPDOWN,
          isEditable: true,
          data: this.lists.metodosAplicacionFertis,
          dropdownConfiguration: {
            valuePrimaryKey: 'codigo_SIEX'
          }
        },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 24
      },
      {
        name: 'codigos_buena_practica',
        vinculatedFields: undefined,
        label: 'Buenas Prácticas Fertilizantes',
        input: {
          type: InputType.MULTISELECT_SEARCH,
          isEditable: true,
          data: this.lists.buenasPracticasFertilizantes,
          dropdownConfiguration: {
            valuePrimaryKey: 'codigo_SIEX'
          }
        },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 25
      },
      {
        name: 'grupo',
        vinculatedFields: undefined,
        label: 'Grupo',
        input: { isEditable: true, type: InputType.TEXT },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.appName === 'agropecuariaramirogruas',
        },
        order: 26
      },
      {
        name: 'tipo_riego',
        vinculatedFields: undefined,
        label: 'Tipo Riego',
        input: { type: InputType.TEXT, isEditable: true },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.appName === 'agropecuariaramirogruas',
        },
        order: 27
      },
      {
        name: 'produccion_estimada',
        vinculatedFields: undefined,
        label: 'Producción Estimada',
        input: { type: InputType.TEXT, isEditable: true },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.appName === 'agropecuariaramirogruas',
        },
        order: 28
      },
      {
        name: 'necesidad_maxima',
        vinculatedFields: undefined,
        label: 'Necesidad Máxima',
        input: { type: InputType.TEXT, isEditable: true },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.appName === 'agropecuariaramirogruas',
        },
        order: 29
      },
      {
        name: 'aporte',
        vinculatedFields: undefined,
        label: 'Aporte',
        input: { type: InputType.TEXT, isEditable: true },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.appName === 'agropecuariaramirogruas',
        },
        order: 30
      },
      {
        name: 'cosecha_obtenida',
        vinculatedFields: undefined,
        label: 'Cosecha Obtenida',
        input: { type: InputType.TEXT, isEditable: true },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.appName === 'agropecuariaramirogruas',
        },
        order: 31
      },
      {
        name: 'estado_tarea',
        vinculatedFields: undefined,
        label: 'Estado de la Tarea',
        input: {
          type: InputType.DROPDOWN,
          isEditable: true,
          data: this.lists.estadoTareas
        },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.appName === 'onda' || this.env.appName === 'lacooperativa',
        },
        order: 32
      },
      {
        name: 'empresa_pagadora',
        vinculatedFields: undefined,
        label: 'Empresa Pagadora',
        input: { type: InputType.TEXT, isEditable: true },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.appName === 'onda' || this.env.appName === 'lacooperativa',
        },
        order: 33
      },
      {
        name: 'recomendaciones_riego',
        vinculatedFields: undefined,
        label: 'Recomendaciones de Riego',
        input: { type: InputType.TEXTAREA, isEditable: true },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.appName === 'agufert',
        },
        order: 34
      },
      {
        name: 'observaciones',
        vinculatedFields: undefined,
        label: 'Observaciones',
        input: {
          type: InputType.TEXTAREA,
          isEditable: true,
          textAreaConfiguration: {
            hasVoiceTranscription: true,
            canTakePhoto: false,
            photoField: undefined
          }
        },
        computed: {
          dependsOn: ['ids_productos'],
          resolve: (context) => this.resolveInhibidorObservation(context.getValue('ids_productos')),
        },
        width: 6,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showTareasFertiObservaciones,
        },
        order: 35
      },
    ];

    if (
      this.env.appName === 'pyf' &&
      this.env.hasClients &&
      this.env.userCanSeeClientes &&
      this.formType === FormType.DUPLICATION
    ) {
      formFieldsSectorLeft.unshift({
        name: 'id_cliente',
        vinculatedFields: undefined,
        label: this.env.clienteName,
        input: {
          type: InputType.DROPDOWN_SEARCH,
          isEditable: true,
          data: this.lists.clientes,
          dropdownConfiguration: {
            valuePrimaryKey: 'id'
          }
        },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: true,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.hasClients
        },
        order: 1
      });
    }
    this.fieldNames = this.fieldNames.concat(formFieldsSectorLeft.filter((it) => it.conditions.isVisible));
    this.fieldLabels = this.fieldLabels.concat(formFieldsSectorLeft.filter((it) => it.conditions.isVisible).map(it => it.label));
    return formFieldsSectorLeft.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  public getFormFieldsBottomLeft(): FormField<any>[] {
    const formFieldsBottomLeft: FormField<any>[] = [
      {
        name: 'ferti_group',
        vinculatedFields: undefined,
        label: '',
        input: {
          type: InputType.REPEATABLE_GROUP,
          isEditable: true,
          repeatableGroupConfiguration: {
            rows: 7,
            addButtonLabel: 'Agregar Producto',
            serialization: { separator: ';' },

            legacyAggregateSerialization: {
              trimTrailingEmptyRows: true
            },

            forceStackedLayout: false,

            bindings: {
              producto: this.formType < 3 ? 'ids_productos' : 'productos_nombres',
              dosis: 'dosis',
              gramos: 'gramos',
              kgHa: 'kg_ha',
            },

            subFields: [
              {
                key: 'producto',
                label: 'Producto Ferti',
                input: {
                  type: InputType.DROPDOWN_SEARCH,
                  isEditable: true,
                  data: this.lists.productos,
                  dropdownConfiguration: {
                    valuePrimaryKey: 'id',
                    filter: createFieldEqualsLegacyFilter<unknown, { readonly tipo?: string }>({
                      fieldName: 'tipo',
                      value: 'fertilizante',
                    }),
                  }
                },
              },
              {
                key: 'dosis',
                label: 'Kg Totales',
                input: { type: InputType.NUMERIC, isEditable: true },
              },
              {
                key: 'gramos',
                label: this.gramosLabel(),
                input: { type: InputType.ONLY_TEXT, isEditable: true },
                isVisible: () => this.env.showTareasFertiGramos,
                computeValue: (ctx: RepeatableComputeContext): string => this.computeGramos(ctx),
              },
              {
                key: 'kgHa',
                label: 'Kg/' + this.env.textoSuperficie,
                input: { type: InputType.ONLY_TEXT, isEditable: true },
                computeValue: (ctx: RepeatableComputeContext): string => this.computeKgHa(ctx),
                isVisible: () => this.env.appName === 'pyf'
              }
            ],
          },
        },
        width: 12,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 1
      }

    ];

    this.fieldNames = this.fieldNames.concat(formFieldsBottomLeft.filter((it) => it.conditions.isVisible));
    this.fieldLabels = this.fieldLabels.concat(formFieldsBottomLeft.filter((it) => it.conditions.isVisible).map(it => it.label));
    return formFieldsBottomLeft.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }


  public getFormFieldsOrdenTrabajo(isSectionVisible: boolean): FormField<any>[] {
    const formFieldsOrdenTrabajo: FormField<any>[] = [
      {
        name: 'orden_trabajo',
        vinculatedFields: undefined,
        label: 'Orden de trabajo',
        input: {
          type: InputType.TEXTAREA,
          isEditable: !checkIfRolAdmin(),
          textAreaConfiguration: {
            hasVoiceTranscription: true,
            canTakePhoto: false,
            photoField: undefined
          }
        },
        width: 0,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: isSectionVisible,
        },
        order: 1
      },
    ];

    this.fieldNames = this.fieldNames.concat(formFieldsOrdenTrabajo.filter((it) => it.conditions.isVisible));
    this.fieldLabels = this.fieldLabels.concat(formFieldsOrdenTrabajo.filter((it) => it.conditions.isVisible).map(it => it.label));
    return formFieldsOrdenTrabajo.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  public getFormFieldsObs(isSectionVisible: boolean): FormField<any>[] {
    const formFieldsObs: FormField<any>[] = [
      {
        name: 'observaciones',
        vinculatedFields: undefined,
        label: 'Observaciones',
        input: {
          type: InputType.TEXTAREA,
          isEditable: true,
          textAreaConfiguration: {
            hasVoiceTranscription: true,
            canTakePhoto: false,
            photoField: undefined
          }
        },
        width: 0,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: isSectionVisible,
        },
        order: 1
      },
    ];

    this.fieldNames = this.fieldNames.concat(formFieldsObs.filter((it) => it.conditions.isVisible));
    this.fieldLabels = this.fieldLabels.concat(formFieldsObs.filter((it) => it.conditions.isVisible).map(it => it.label));
    return formFieldsObs.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }


  private getFormSections(): FormSection<TareaFertiModel>[] {
    const sections: FormSection<TareaFertiModel>[] = [
      {
        name: '',
        width: 12,
        isVisible: true,
        fields: this.getFormFieldsSectorLeft(),
        order: 1
      },
      {
        name: 'Productos abonado',
        width: 6,
        isVisible: true,
        fields: this.getFormFieldsBottomLeft(),
        tables: this.appendFertilizantesTables(),
        order: 2
      },
      {
        name: '',
        width: 6,
        isVisible: this.env.showTareasMto,
        fields: this.getFormFieldsOrdenTrabajo(this.env.showTareasMto),
        order: 3
      },
      {
        name: '',
        width: 6,
        isVisible: this.env.showTareasMto,
        fields: this.getFormFieldsObs(this.env.showTareasMto),
        order: 4
      },
      {
        name: '',
        width: 6,
        isVisible: true,
        fields: this.getHiddenFields(),
        order: 5
      },
    ];

    return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  private getHiddenFields(): FormField<any>[] {
    const hiddenFields: FormField<any>[] = [
      {
        name: 'id',
        vinculatedFields: undefined,
        label: '',
        input: {
          type: InputType.HIDDEN,
          defaultValue: this.model.id,
          isEditable: false
        },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 1
      },
      {
        name: 'id_cliente',
        vinculatedFields: undefined,
        label: '',
        input: {
          type: InputType.HIDDEN,
          defaultValue: this.resolveClientId(this.model.id_finca),
          isEditable: false
        },
        computed: {
          dependsOn: ['id_finca'],
          resolve: (context) => this.resolveClientId(context.getValue('id_finca')),
        },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.hasClients && this.env.userCanSeeClientes
        },
        order: 2
      },
      {
        name: 'id_usuario',
        vinculatedFields: undefined,
        label: '',
        input: {
          type: InputType.HIDDEN,
          defaultValue: this.resolveUserId(this.model.id_finca),
          isEditable: false
        },
        computed: {
          dependsOn: ['id_finca'],
          resolve: (context) => this.resolveUserId(context.getValue('id_finca')),
        },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true
        },
        order: 3
      },
      {
        name: 'cultivo',
        vinculatedFields: undefined,
        label: '',
        input: { type: InputType.HIDDEN, isEditable: false },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true
        },
        computed: {
          dependsOn: ['id_parcelas'],
          resolve: (context) => this.joinParcelasFieldFromValue(
            context.getValue('id_parcelas'),
            'cultivo',
            'cultivo',
          ),
        },
        order: 4
      },
      {
        name: 'variedad',
        vinculatedFields: undefined,
        label: '',
        input: { type: InputType.HIDDEN, isEditable: false },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true
        },
        computed: {
          dependsOn: ['id_parcelas'],
          resolve: (context) => this.joinParcelasFieldFromValue(
            context.getValue('id_parcelas'),
            'variedad',
            'variedad',
          ),
        },
        order: 5
      },
      {
        name: 'sup_cultivada',
        vinculatedFields: undefined,
        label: '',
        input: { type: InputType.HIDDEN, isEditable: false },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true
        },
        computed: {
          dependsOn: ['id_parcelas'],
          resolve: (context) => this.joinParcelasFieldFromValue(
            context.getValue('id_parcelas'),
            'superficie_cultivada',
            'sup_cultivada',
          ),
        },
        order: 6
      },
      {
        name: 'signature_img',
        vinculatedFields: undefined,
        label: '',
        input: { type: InputType.HIDDEN, isEditable: false },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showTareasMto,
        },
        order: 6
      },
      {
        name: 'imagen',
        vinculatedFields: undefined,
        label: '',
        input: { type: InputType.HIDDEN, isEditable: false },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showTareasMto
        },
        order: 7
      },
      {
        name: 'image_after',
        vinculatedFields: undefined,
        label: '',
        input: { type: InputType.HIDDEN, isEditable: false },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showTareasMto
        },
        order: 8
      },
      {
        name: 'asesor',
        vinculatedFields: undefined,
        label: '',
        input: {
          type: InputType.HIDDEN,
          isEditable: false,
          defaultValue: StorageManager.getUser().nombre
        },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true
        },
        order: 9
      },
      {
        name: 'horas_during',
        vinculatedFields: undefined,
        label: '',
        input: {
          type: InputType.HIDDEN,
          isEditable: false,
        },
        computed: {
          dependsOn: ['ids_sectores'],
          resolve: (context) => this.joinSectoresFieldFromValue(
            context.getValue('ids_sectores'),
            'tiempoPostRiego',
          ),
        },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true
        },
        order: 10
      },
      {
        name: 'horas_post',
        vinculatedFields: undefined,
        label: '',
        input: {
          type: InputType.HIDDEN,
          isEditable: false,
        },
        computed: {
          dependsOn: ['ids_sectores'],
          resolve: (context) => this.joinSectoresFieldFromValue(
            context.getValue('ids_sectores'),
            'tiempoPostRiego',
          ),
        },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true
        },
        order: 10
      },
      {
        name: 'n',
        vinculatedFields: undefined,
        label: '',
        input: {
          type: InputType.HIDDEN,
          isEditable: false,
        },
        computed: {
          dependsOn: ['ferti_group', 'id_parcelas', 'ids_sectores'],
          resolve: () => this.sumNpk('n'),
        },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true
        },
        order: 11
      },
      {
        name: 'p',
        vinculatedFields: undefined,
        label: '',
        input: {
          type: InputType.HIDDEN,
          isEditable: false,
        },
        computed: {
          dependsOn: ['ferti_group', 'id_parcelas', 'ids_sectores'],
          resolve: () => this.sumNpk('p'),
        },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true
        },
        order: 12
      },
      {
        name: 'k',
        vinculatedFields: undefined,
        label: '',
        input: {
          type: InputType.HIDDEN,
          isEditable: false,
        },
        computed: {
          dependsOn: ['ferti_group', 'id_parcelas', 'ids_sectores'],
          resolve: () => this.sumNpk('k'),
        },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true
        },
        order: 13
      },
      {
        name: 'ca',
        vinculatedFields: undefined,
        label: '',
        input: {
          type: InputType.HIDDEN,
          isEditable: false,
        },
        computed: {
          dependsOn: ['ferti_group', 'id_parcelas', 'ids_sectores'],
          resolve: () => this.sumNpk('ca'),
        },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showFertilizantesCa
        },
        order: 14
      },
      {
        name: 's',
        vinculatedFields: undefined,
        label: '',
        input: {
          type: InputType.HIDDEN,
          isEditable: false,
        },
        computed: {
          dependsOn: ['ferti_group', 'id_parcelas', 'ids_sectores'],
          resolve: () => this.sumNpk('s'),
        },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showFertilizantesS
        },
        order: 15
      },
      {
        name: 'mg',
        vinculatedFields: undefined,
        label: '',
        input: {
          type: InputType.HIDDEN,
          isEditable: false,
        },
        computed: {
          dependsOn: ['ferti_group', 'id_parcelas', 'ids_sectores'],
          resolve: () => this.sumNpk('mg'),
        },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showFertilizantesMg
        },
        order: 16
      },
    ];

    return hiddenFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }


  private resolveInhibidorObservation(selectedProductsValue: unknown): string | undefined {
    const selectedProducts = this.resolveDropdownRawValues(this.lists.productos, selectedProductsValue);

    if (selectedProducts.some((product) => this.getObjectProperty(product, 'abono_con_inhibidor') === 1)) {
      return 'Con inhibidor de Nitrógeno';
    }

    return undefined;
  }


  /** Resolve the current client id from the selected finca without depending on DropdownData.selected. */
  private resolveClientId(fincaValue: unknown): unknown {
    const clientId = this.cliente?.id ??
      this.resolveFincaProperty(fincaValue, 'id_cliente') ??
      this.getModelTextValue('id_cliente');

    return clientId === '' ? undefined : clientId;
  }

  /** Resolve the current user id from the selected finca without depending on DropdownData.selected. */
  private resolveUserId(fincaValue: unknown): unknown {
    if (!this.env.showTareasMto) {
      const userId = this.usuario?.id ??
        this.resolveFincaProperty(fincaValue, 'id_usuario') ??
        this.getModelTextValue('id_usuario');

      return userId === '' ? undefined : userId;
    }

    if (this.formType !== FormType.EDITION) {
      return this.usuario?.id;
    }

    const storedUserId = this.getModelTextValue('id_usuario');

    return storedUserId === '' ? undefined : storedUserId;
  }

  /** Resolve a finca property from either the current control value or the option catalog. */
  private resolveFincaProperty(fincaValue: unknown, propertyName: keyof FincasModel): unknown {
    const directValue = this.getObjectProperty(fincaValue, String(propertyName));

    if (directValue !== null && directValue !== undefined && directValue !== '') {
      return directValue;
    }

    return this.getObjectProperty(
      this.findDropdownRawValue(this.lists.fincas, fincaValue),
      String(propertyName),
    );
  }

  /** Build semicolon-separated hidden values from the current FormGroup value first. */
  private joinParcelasFieldFromValue(selectedValue: unknown, fieldName: keyof ParcelasModel, fallbackFieldName: string): string {
    const selectedParcelas = this.resolveDropdownRawValues(this.lists.parcelas, selectedValue);

    if (selectedParcelas.length === 0) {
      return this.getModelTextValue(fallbackFieldName);
    }

    return selectedParcelas
      .map((parcela) => this.getObjectProperty(parcela, String(fieldName)))
      .filter((value) => value !== null && value !== undefined && value !== '')
      .map(String)
      .join(';');
  }

  /** Build semicolon-separated sector values from the current FormGroup value first. */
  private joinSectoresFieldFromValue(selectedValue: unknown, fieldName: keyof SectorModel): string {
    const selectedSectores = this.resolveDropdownRawValues(this.lists.sectores, selectedValue);

    if (selectedSectores.length <= 1) {
      return '';
    }

    return selectedSectores
      .map((sector) => this.getObjectProperty(sector, String(fieldName)))
      .filter((value) => value !== null && value !== undefined && value !== '')
      .map(String)
      .join(';');
  }

  /** Resolve one or multiple dropdown raw entities from primitive or object control values. */
  private resolveDropdownRawValues(
    dropdownData: { values?: Array<{ value?: unknown; raw?: unknown; valueAliases?: readonly unknown[] }> },
    selectedValue: unknown,
  ): unknown[] {
    const selectedValues = this.normalizeSelectedValues(selectedValue);

    return selectedValues
      .map((value) => this.findDropdownRawValue(dropdownData, value) ?? value)
      .filter((value) => typeof value === 'object' && value !== null);
  }

  private normalizeSelectedValues(selectedValue: unknown): unknown[] {
    if (Array.isArray(selectedValue)) {
      return selectedValue;
    }

    if (typeof selectedValue === 'string' && selectedValue.includes(';')) {
      return selectedValue
        .split(';')
        .map((value) => value.trim())
        .filter((value) => value.length > 0);
    }

    return [selectedValue];
  }

  /** Resolve the raw option entity without reading mutable selected state. */
  private findDropdownRawValue(
    dropdownData: { values?: Array<{ value?: unknown; raw?: unknown; valueAliases?: readonly unknown[] }> },
    selectedValue: unknown,
  ): unknown {
    if (selectedValue === null || selectedValue === undefined || selectedValue === '') {
      return null;
    }

    if (typeof selectedValue === 'object') {
      return selectedValue;
    }

    const matchedOption = dropdownData.values?.find((option) => this.matchesDropdownOption(selectedValue, option));

    return matchedOption?.raw ?? matchedOption?.value ?? null;
  }

  /** Match primitive control values against canonical value, aliases and raw entity id. */
  private matchesDropdownOption(
    selectedValue: unknown,
    option: { value?: unknown; raw?: unknown; valueAliases?: readonly unknown[] },
  ): boolean {
    return this.valuesMatch(selectedValue, option.value) ||
      this.valuesMatch(selectedValue, this.getObjectProperty(option.raw, 'id')) ||
      (option.valueAliases ?? []).some((alias) => this.valuesMatch(selectedValue, alias));
  }

  /** Read a property from unknown raw entities safely. */
  private getObjectProperty(value: unknown, propertyName: string): unknown {
    if (typeof value !== 'object' || value === null) {
      return null;
    }

    return (value as Record<string, unknown>)[propertyName] ?? null;
  }

  /** Compare dropdown ids consistently when some catalogs use numeric ids and others strings. */
  private valuesMatch(left: unknown, right: unknown): boolean {
    if (left === null || left === undefined || right === null || right === undefined) {
      return false;
    }

    return left === right || String(left) === String(right);
  }

  /** Keep stored hidden values while dropdown options are still loading. */
  private getModelTextValue(fieldName: string): string {
    const value = (this.model as Record<string, unknown>)[fieldName];

    if (value === null || value === undefined) {
      return '';
    }

    if (Array.isArray(value)) {
      return value.map(String).join(';');
    }

    return value.toString();
  }

  private formatoHorasMinutos(horaEntrada: string, horaSalida: string): string {

    if (!this.isValidTime(horaEntrada) || !this.isValidTime(horaSalida)) {
      console.error('El formato de hora no es válido. Asegúrese de que el formato sea HH:mm');
      return '00:00';
    }

    const duracion = moment.duration(moment(horaSalida, 'HH:mm').diff(moment(horaEntrada, 'HH:mm')));

    return `${this.padZero(2, Math.floor(duracion.asHours()).toString())}:${this.padZero(2, duracion.minutes().toString())}`;
  }

  private isValidTime(hora: string): boolean {
    return moment(hora, 'HH:mm', true).isValid();
  }

  private padZero(maxLength: number, numero: string): string {
    return numero.padStart(maxLength, '0');
  }


  private appendFertilizantesTables(): FormTable[] {
    const showCa = !!this.env.showFertilizantesCa;
    const showS = !!this.env.showFertilizantesS;
    const showMg = !!this.env.showFertilizantesMg;

    const tables: FormTable[] = [];

    const headerBg = '#c0c0c0';
    const bodyBg = '';

    const nutrientsTitle = ['N', 'P', 'K'];
    if (showCa) nutrientsTitle.push('Ca');
    if (showS) nutrientsTitle.push('S');
    if (showMg) nutrientsTitle.push('Mg');

    // ----------------------------
    // 1) Totals table (N/P/K/Ca/S/Mg)
    // ----------------------------
    if (this.env.showFertilizantesNPK) {
      const header: string[] = ['N', 'P', 'K'];
      if (showCa) header.push('Ca');
      if (showS) header.push('S');
      if (showMg) header.push('Mg');

      const row: string[] = [
        this.npkBuilder.totalsVm.n,
        this.npkBuilder.totalsVm.p,
        this.npkBuilder.totalsVm.k,
      ];

      if (showCa) row.push(this.npkBuilder.totalsVm.ca ?? this.zeroLabel);
      if (showS) row.push(this.npkBuilder.totalsVm.s ?? this.zeroLabel);
      if (showMg) row.push(this.npkBuilder.totalsVm.mg ?? this.zeroLabel);

      tables.push({
        title: `${nutrientsTitle.join(' / ')} Abonado`,
        showIndex: false,
        data: [header, row],
        style: [headerBg, bodyBg],
      });
    }

    // ----------------------------
    // 2) Units table (Sector OR Parcela)
    // ----------------------------
    const unitsTitle =
      this.env.appName === 'torrepacheco'
        ? 'Uds Fertilizantes (Ha/Campaña)'
        : 'Unidades fertilizantes';

    if (!this.env.udsByParcela) {
      // Sector table
      const sectorHeader: string[] = [
        this.env.sectorName ?? 'Sector',
        `Superficie ${this.env.textoSuperficie ?? ''}`.trim(),
        'N',
        'P',
        'K',
      ];
      if (showCa) sectorHeader.push('Ca');
      if (showS) sectorHeader.push('S');
      if (showMg) sectorHeader.push('Mg');

      const data: string[][] = [sectorHeader];
      const styles: string[] = [headerBg];

      for (const rowVm of this.npkBuilder.sectorRowsVm) {
        const row: string[] = [
          rowVm.name,
          rowVm.superficieLabel,
          rowVm.n,
          rowVm.p,
          rowVm.k,
        ];

        if (showCa) row.push(rowVm.ca ?? '-');
        if (showS) row.push(rowVm.s ?? '-');
        if (showMg) row.push(rowVm.mg ?? '-');

        data.push(row);
        styles.push(bodyBg);
      }

      // Total row (only if UI would show it)
      if (this.npkBuilder.sectorRowsVm.length > 1 && this.env.appName !== 'pyf') {
        const totalRow: string[] = [
          'Total',
          this.npkBuilder.totalsVm.superficie,
          this.npkBuilder.totalsVm.n,
          this.npkBuilder.totalsVm.p,
          this.npkBuilder.totalsVm.k,
        ];

        if (showCa) totalRow.push(this.npkBuilder.totalsVm.ca ?? this.zeroLabel);
        if (showS) totalRow.push(this.npkBuilder.totalsVm.s ?? this.zeroLabel);
        if (showMg) totalRow.push(this.npkBuilder.totalsVm.mg ?? this.zeroLabel);

        data.push(totalRow);
        styles.push(bodyBg);
      }

      tables.push({
        title: unitsTitle,
        showIndex: false,
        data,
        style: styles,
      });
    } else {
      // Parcela table
      const parcelaHeader: string[] = [
        'Parcela',
        `Superficie ${this.env.textoSuperficie ?? ''}`.trim(),
        'N',
        'P',
        'K',
      ];
      if (showCa) parcelaHeader.push('Ca');
      if (showS) parcelaHeader.push('S');
      if (showMg) parcelaHeader.push('Mg');

      const data: string[][] = [parcelaHeader];
      const styles: string[] = [headerBg];

      for (const rowVm of this.npkBuilder.parcelaRowsVm) {
        const row: string[] = [
          rowVm.name,
          rowVm.superficieLabel,
          rowVm.n,
          rowVm.p,
          rowVm.k,
        ];

        if (showCa) row.push(rowVm.ca ?? '-');
        if (showS) row.push(rowVm.s ?? '-');
        if (showMg) row.push(rowVm.mg ?? '-');

        data.push(row);
        styles.push(bodyBg);
      }

      // Total row (solo si hay más de una parcela)
      if (this.npkBuilder.parcelaRowsVm.length > 1) {
        const totalRow: string[] = [
          'Total (todas las parcelas)',
          this.npkBuilder.parcelaTotalsVm.superficie,
          this.npkBuilder.parcelaTotalsVm.n,
          this.npkBuilder.parcelaTotalsVm.p,
          this.npkBuilder.parcelaTotalsVm.k,
        ];

        if (showCa) totalRow.push(this.npkBuilder.parcelaTotalsVm.ca ?? this.zeroLabel);
        if (showS)  totalRow.push(this.npkBuilder.parcelaTotalsVm.s  ?? this.zeroLabel);
        if (showMg) totalRow.push(this.npkBuilder.parcelaTotalsVm.mg ?? this.zeroLabel);

        data.push(totalRow);
        styles.push(bodyBg);
      }

      tables.push({
        title: unitsTitle,
        showIndex: false,
        data,
        style: styles,
      });
    }

    return tables;
  }


  private gramosLabel(): string {
    return this.env.showKilosHectarea
      ? `Kg/${this.env.textoSuperficie}` // prosanzcu meaning
      : ['abonosfolques', 'pyf'].includes(this.env.appName)
        ? 'Gr./árbol'
        : this.env.appName === 'ferticinca'
          ? `(Dosis/${this.env.textoSuperficie})`
          : this.env.appName === 'ingenia'
            ? 'Gr./cc por Árbol'
            : `Gr./árbol (dosis/${this.env.textoSuperficie})`;
  }

  private sumCommaSeparatedFloats(value: unknown): number {
    if (value == null) {
      return 0;
    }

    const str = String(value).trim();
    if (!str) {
      return 0;
    }

    let sum = 0;

    // Accepts "1,25;0,75;2" or "1.25;0.75;2"
    const parts = str.split(';');

    for (let i = 0; i < parts.length; i++) {
      const n = Number(parts[i]?.replace(',', '.'));
      if (Number.isFinite(n)) {
        sum += n;
      }
    }

    return sum;
  }

  private getTotalArboles(model: FormGroup): number {
      if (this.env.showFertilizantesParcelas) {
          return this.resolveDropdownRawValues(this.lists.parcelas, model.get('id_parcelas')?.value)
              .filter((parcela): parcela is ParcelasModel => this.isParcelasModel(parcela) && Boolean(parcela.plantas))
              .map((parcela: ParcelasModel) => Number.parseInt(parcela.plantas ?? '0', 10))
              .reduce((total: number, plantas: number) => total + plantas, 0);
      }

      return this.resolveDropdownRawValues(this.lists.sectores, model.get('ids_sectores')?.value)
          .filter((sector): sector is SectorModel => this.isSectorModel(sector) && Boolean(sector.plantas))
          .map((sector: SectorModel) => Number.parseInt(sector.plantas ?? '0', 10))
          .reduce((total: number, plantas: number) => total + plantas, 0);
  }

  private isParcelasModel(value: unknown): value is ParcelasModel {
      return typeof value === 'object' && value !== null && 'plantas' in value;
  }

  private isSectorModel(value: unknown): value is SectorModel {
      return typeof value === 'object' && value !== null && 'plantas' in value;
  }

  private computeGramos(ctx: RepeatableComputeContext): string {
    const dosis = this.toNum(ctx.rows[ctx.rowIndex]?.['dosis']);

    if (!(dosis > 0)) {
      return '0';
    }

    const totalHectareas = this.npkBuilder.getTotalSuperficieParcelas();

    // PROSANZCU/showKilosHectarea: "gramos" field is actually kg/ha (3 decimals)
    if (this.env.showKilosHectarea) {
      if (!(totalHectareas > 0)) {
        return '0';
      }

      return (dosis / totalHectareas).toFixed(3);
    }

    const totalArboles = this.getTotalArboles(ctx.model);

    if (!(totalArboles > 0)) {
      return '0';
    }

    const gramosArbol =
      this.env.appName === 'abonosfolques' && totalHectareas > 0
        ? (dosis / totalArboles) * totalHectareas * 1000
        : (dosis / totalArboles) * 1000;

    return gramosArbol.toFixed(2);
  }

  private computeKgHa(ctx: RepeatableComputeContext): string {
    const dosis = this.toNum(ctx.rows[ctx.rowIndex]?.['dosis']);

    const superficieTotal = this.sumCommaSeparatedFloats(ctx.model.get('sup_cultivada')?.value);

    if (!(superficieTotal > 0)) {
      return Utils.decimalFormat(0, 2, '.', ',', 2) || '0,00';
    }

    return Utils.decimalFormat(dosis / superficieTotal, 2, '.', ',', 2) || '0,00';
  }

  /** Parse EU/US-formatted numbers safely: "1.432,21" -> 1432.21, "1432.21" -> 1432.21 */
  private toNum(value: unknown): number {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : 0;
    }

    let s = String(value ?? '').trim();
    if (!s) return 0;

    // If the field ever contains multi-values like "30,42;247,03", take the first token.
    s = s.split(';')[0]?.trim() ?? '';

    // Normalize NBSP and spaces
    s = s.replace(/\u00A0/g, '').replace(/\s+/g, '');

    // If both '.' and ',' appear, assume '.' = thousands and ',' = decimal (EU style)
    if (s.includes('.') && s.includes(',')) {
      s = s.replace(/\./g, '').replace(',', '.');
    } else {
      // Remove all separators except the last one, then ensure decimal is '.'
      s = s.replace(/[.,](?=.*[.,])/g, '');
      s = s.replace(',', '.');
    }

    const n = Number(s);
    return Number.isFinite(n) ? n : 0;
  }

  /** Sum N/P/K, returning a plain number (format only for display elsewhere) */
  private sumNpk(field: 'n' | 'p' | 'k' | 'ca' | 's' | 'mg'): number {
    if (this.env.appName === 'avafitogest') {
      const fieldMap: Record<string, number> = {
        n:  this.npkBuilder.totalN,
        p:  this.npkBuilder.totalP,
        k:  this.npkBuilder.totalK,
        ca: this.npkBuilder.totalCa,
        s:  this.npkBuilder.totalS,
        mg: this.npkBuilder.totalMg,
      };
      const rawValue = fieldMap[field] ?? 0;
      const totalSuperficie = this.npkBuilder.getTotalSuperficieParcelas();
      return totalSuperficie > 1 ? rawValue / totalSuperficie : rawValue;
    }

    const rows = this.env.udsByParcela
      ? this.npkBuilder.parcelaRowsVm
      : this.npkBuilder.sectorRowsVm;

    return rows.reduce((acc, row) => acc + this.toNum((row as any)[field]), 0);
  }

  public refreshFertilizantesTablesFromNpk(): void {
    const sections = this.formFields?.sections ?? [];
    const target = sections.find((s) => s.name === 'Productos abonado'); // or order === 2

    if (!target) return;

    target.tables = this.appendFertilizantesTables();
  }


  public setLocationDataSources(endpoints: { fincas: unknown; sectores: unknown; parcelas: unknown }): void {
    const fincaField = this.findField(this.formType < 3 ? 'id_finca' : 'finca');
    const sectorField = this.findField(this.formType < 3 ? 'ids_sectores' : 'sectores_nombres');
    const parcelaField = this.findField(this.formType < 3 ? 'id_parcelas' : 'parcelas_nombres');

    if (fincaField) {
      fincaField.dataSource = createTareasLocationDropdownDataSource<TareaFertiModel, FincasModel>({
        endpoint: endpoints.fincas,
        cacheKey: 'tareas-ferti-fincas',
        label: 'nombre',
        params: createClientScopedParams,
        filterOption: createClientScopedOptionFilter<TareaFertiModel>({
          isEnabled: () => this.env.hasClients && this.formType !== FormType.EDITION && this.env.userCanSeeClientes,
        }),
      });
      fincaField.input.dropdownConfiguration = {
        ...fincaField.input.dropdownConfiguration,
        filter: createClientScopedLegacyFilter<TareaFertiModel>({
          isEnabled: () => this.env.hasClients && this.formType !== FormType.EDITION && this.env.userCanSeeClientes,
        }),
      };
      fincaField.dependencyBehaviour = tareasLocationFincaDependencyBehaviour;
    }

    if (sectorField) {
      sectorField.dataSource = createTareasLocationDropdownDataSource<TareaFertiModel, SectorModel>({
        endpoint: endpoints.sectores,
        cacheKey: 'tareas-ferti-sectores',
        label: 'nombre',
        dependsOn: ['id_finca'],
        params: createFincaScopedParams,
        filterOption: createFincaScopedOptionFilter<TareaFertiModel>(),
      });
      sectorField.input.dropdownConfiguration = {
        ...sectorField.input.dropdownConfiguration,
        filter: createFincaScopedLegacyFilter<TareaFertiModel>(),
      };
      sectorField.dependencyBehaviour = tareasLocationDependencyBehaviour;
    }

    if (parcelaField) {
      parcelaField.dataSource = createTareasLocationDropdownDataSource<TareaFertiModel, ParcelasModel>({
        endpoint: endpoints.parcelas,
        cacheKey: 'tareas-ferti-parcelas',
        label: (parcela) => this.getParcelaOptionLabel(parcela),
        dependsOn: ['id_finca'],
        params: createFincaScopedParams,
        filterOption: createParcelasScopedOptionFilter<TareaFertiModel>({
          sectorFieldName: 'ids_sectores',
          isSectorFilterEnabled: () => this.env.showFertilizantesSectores,
        }),
      });
      parcelaField.input.dropdownConfiguration = {
        ...parcelaField.input.dropdownConfiguration,
        filter: createParcelasScopedLegacyFilter<TareaFertiModel>({
          sectorFieldName: 'ids_sectores',
          isSectorFilterEnabled: () => this.env.showFertilizantesSectores,
        }),
      };
      parcelaField.dependencyBehaviour = tareasLocationDependencyBehaviour;
    }
  }

  private findField(fieldName: string): FormField<TareaFertiModel> | undefined {
    return this.formFields.sections
      .flatMap((section) => section.fields)
      .find((field) => field.name === fieldName) as FormField<TareaFertiModel> | undefined;
  }

  private getParcelaOptionLabel(parcela: ParcelasModel): string {
    if (this.env.appName !== 'avafitogest') {
      return String(parcela.nombre ?? '');
    }

    const nombre = String(parcela.nombre ?? '').trim();
    const variedad = String(parcela.variedad ?? 'SIN VARIEDAD').trim();
    const zonaRiego = String(parcela.zona_riego ?? '').toLowerCase();
    const zonaRiegoAbreviada = String(parcela.cultivo ?? '').toLowerCase() === 'arroz'
      ? zonaRiego.includes('alta')
        ? 'ZA'
        : zonaRiego.includes('baja')
          ? 'ZB'
          : ''
      : '';
    const variedadConZona = zonaRiegoAbreviada ? `${variedad} - ${zonaRiegoAbreviada}` : variedad;
    const codigos = [parcela.poligono, parcela.numero, parcela.recinto]
      .map((value) => String(value ?? '').trim())
      .filter((value) => value.length > 0)
      .join('-');
    const superficieRaw = String(parcela.superficie_cultivada ?? parcela.superficie_sigpac ?? parcela.superficie ?? '0');
    const superficie = parseFloat(superficieRaw.replace(',', '.')) || 0;
    const superficieFormateada = superficie.toLocaleString('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const derecha = [codigos || undefined, `${superficieFormateada} ${this.env.textoSuperficie || 'ha'}`]
      .filter((value) => value !== undefined)
      .join(' · ');

    return `${nombre} - ${variedadConZona} — ${derecha}`;
  }

  constructor(
    type: FormType,
    initialModel: any,
    table: (string[] | string[][])[] | null,
    disabledDates: Date[],
    private readonly eventos: { start: string; title: string; hora_inicio: string; hora_fin: string }[]
  ) {
    this.formType = +type;
    this.disabledDates = disabledDates;

    if (initialModel) {
      this.model = { ...this.model, ...initialModel };
    }

    if (!this.model.ids_productos) this.model.ids_productos = '';
    if (!this.model.dosis) this.model.dosis = '';

    this.formFields = {
      name: 'fertilizantes',
      header: 'Tarea Fertilizantes',
      type: this.formType,
      sections: this.getFormSections(),
      table: { title: '', showIndex: false, data: table?.[0], style: table?.[1] },
      uploadButton: {
        isEnabled: false,
        label: '',
      },
      signed: this.env.showTareasMto
    };
  }
}